import { useState, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import MapView from './components/MapView'
import AddressSearchMap from './components/AddressSearchMap'
import NewDealForm from './components/NewDealForm'
import ReviewQueue from './components/ReviewQueue'
import ViewSelector from './components/ViewSelector'
import FilterPanel from './components/FilterPanel'
import StatsPanel from './components/StatsPanel'
import DealSidebar from './components/DealSidebar'
import LoginGate from './components/LoginGate'
import { useDeals, useMe } from './lib/hooks'

function AppShell() {
  const queryClient = useQueryClient()
  const { data: deals = [], isLoading, isError, error } = useDeals()
  const { data: me } = useMe()
  // In local dev (SDB_LOCAL_DEV=1), we bypass Entra entirely — grant all UI
  // affordances so we can exercise the Review Queue path without SWA cookies.
  const localBypass = import.meta.env.VITE_LOCAL_DEV === '1'
  // SWA normalizes assigned role names to lowercase server-side, so compare
  // case-insensitively against the userRoles claim from /.auth/me.
  const canReview = localBypass || !!me?.userRoles?.some(r => {
    const lo = r?.toLowerCase()
    return lo === 'editor' || lo === 'admin'
  })
  const [selectedDeal, setSelected]   = useState(null)
  const [currentView, setCurrentView] = useState('classic')
  const [filters, setFilters]         = useState({
    types:     ['acquisition', 'development', 'lending'],
    statuses:  [],
    states:    [],
    search:    '',
    dateRange: 'all',   // 'all' | '7d' | '30d' | '90d' | 'ytd'
  })

  // Compute the YYYY-MM-DD cutoff for the currently-selected dateRange filter.
  // Returns null when 'all'. Compared against deal.date_sourced (a business
  // date from dim_date joined via created_date_key), not the row-insert
  // timestamp — those cluster on bulk-import days and are not meaningful for
  // "deals sourced in last N days".
  const dateCutoff = useMemo(() => {
    const now = new Date()
    const daysAgo = d => new Date(now.getTime() - d * 864e5).toISOString().slice(0, 10)
    switch (filters.dateRange) {
      case '7d':  return daysAgo(7)
      case '30d': return daysAgo(30)
      case '90d': return daysAgo(90)
      case 'ytd': return `${now.getFullYear()}-01-01`
      default:    return null
    }
  }, [filters.dateRange])

  const filteredDeals = useMemo(() => {
    const q = filters.search.toLowerCase().trim()
    return deals.filter(d => {
      if (!filters.types.includes(d.type)) return false
      if (filters.statuses.length && !filters.statuses.includes(d.status?.toLowerCase())) return false
      if (filters.states.length && !filters.states.includes(d.state)) return false
      if (dateCutoff) {
        // date_sourced is YYYY-MM-DD; string compare works. Deals with no
        // date_sourced are excluded when a range is active — we can't verify
        // they fall in the window.
        if (!d.date_sourced || d.date_sourced < dateCutoff) return false
      }
      if (q) {
        const hay = `${d.name || ''} ${d.address || ''} ${d.city || ''} ${d.state || ''} ${d.org || ''}`.toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    })
  }, [deals, filters, dateCutoff])

  // ── Loading ────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', height: '100vh', background: '#0A192F', gap: 16,
      }}>
        <div style={{
          width: 28, height: 28, borderRadius: '50%', border: '2px solid #D4AF37',
          borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite',
        }} />
        <p style={{ color: '#94A3B8', fontSize: 11, letterSpacing: '0.12em' }}>LOADING PIPELINE DATA</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    )
  }

  if (isError) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', height: '100vh', background: '#0A192F', gap: 12,
      }}>
        <p style={{ color: '#E74C3C', fontWeight: 600 }}>Could not load /api/deals</p>
        <p style={{ color: '#94A3B8', fontSize: 12, maxWidth: 500, textAlign: 'center' }}>
          {error?.message || 'Check that the Function App is running and that you\'re signed in.'}
        </p>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#0A192F', overflow: 'hidden' }}>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <header style={{
        height: 52, flexShrink: 0,
        display: 'flex', alignItems: 'center', gap: 20, padding: '0 20px',
        background: '#061020',
        borderBottom: '1px solid rgba(212,175,55,0.2)',
      }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <div style={{
            width: 30, height: 30, borderRadius: 6, flexShrink: 0,
            background: 'linear-gradient(135deg, #D4AF37 0%, #C5A059 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 15, fontWeight: 800, color: '#0A192F',
          }}>P</div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#FFFFFF', letterSpacing: '0.1em' }}>
              PSG DEAL INTELLIGENCE
            </div>
            <div style={{ fontSize: 9, color: '#94A3B8', letterSpacing: '0.15em', marginTop: 1 }}>
              SELF-STORAGE PIPELINE · v2
            </div>
          </div>
        </div>

        {/* Search */}
        <div style={{ position: 'relative', width: 320, flexShrink: 0 }}>
          <span style={{
            position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)',
            color: '#94A3B8', fontSize: 14, pointerEvents: 'none',
          }}>⌕</span>
          <input
            type="text"
            placeholder="Search deal, address, org..."
            value={filters.search}
            onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '6px 10px 6px 28px',
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 6, color: '#FFFFFF', fontSize: 12,
              outline: 'none', letterSpacing: '0.02em',
              transition: 'border-color 0.15s',
            }}
          />
        </div>

        {/* Stats */}
        <StatsPanel deals={deals} filteredDeals={filteredDeals} />

        {/* Date */}
        <div style={{
          marginLeft: 'auto', color: '#94A3B8', fontSize: 10,
          letterSpacing: '0.1em', flexShrink: 0,
        }}>
          {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).toUpperCase()}
        </div>
      </header>

      {/* ── Body ───────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>

        {/* View Selector Tabs */}
        <ViewSelector currentView={currentView} onViewChange={setCurrentView} canReview={canReview} />

        <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
          {currentView === 'new-deal' && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <NewDealForm
                onSubmitted={() => {
                  queryClient.invalidateQueries({ queryKey: ['intake'] })
                  // Deals don't refresh — nothing shows on the map until an
                  // Editor/Admin approves in Review Queue.
                }}
              />
            </div>
          )}

          {currentView === 'review-queue' && canReview && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <ReviewQueue />
            </div>
          )}

          {currentView === 'classic' && (
            <>
              <FilterPanel deals={deals} filters={filters} onFiltersChange={setFilters} />

              <div style={{ flex: 1, position: 'relative', minWidth: 0 }}>
                <MapView
                  deals={filteredDeals}
                  selectedDeal={selectedDeal}
                  onSelectDeal={setSelected}
                />
              </div>
            </>
          )}

          {currentView === 'address-search' && (
            <div style={{ flex: 1, position: 'relative', minWidth: 0 }}>
              <AddressSearchMap
                deals={deals}
                selectedDeal={selectedDeal}
                onSelectDeal={setSelected}
                onClose={() => setSelected(null)}
              />
            </div>
          )}

          {selectedDeal && (
            <DealSidebar
              deal={selectedDeal}
              canDelete={canReview}
              onClose={() => setSelected(null)}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default function App() {
  return (
    <LoginGate>
      <AppShell />
    </LoginGate>
  )
}
